import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import bannerImage from "../img/front/BannierreHeader.png";
import logoFront from "../img/front/logo_front2.png";

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user, authLoading } = useAuth();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactPromptOpen, setIsContactPromptOpen] = useState(false);

  const profileRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest("[data-mobile-toggle]")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
    setIsContactPromptOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isContactPromptOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isContactPromptOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleContactClick = (e) => {
    if (authLoading) {
      e.preventDefault();
      return;
    }

    if (!isAuthenticated) {
      e.preventDefault();
      setIsContactPromptOpen(true);
    }
  };

  const isExactActive = (path) => location.pathname === path;
  const isSectionActive = (path) => location.pathname.startsWith(path);

  const navLinkClass = (active) =>
    `relative transition duration-300 ${
      active ? "text-orange-500" : "text-white hover:text-orange-500"
    }`;

  const navUnderlineClass = (active) =>
    `absolute left-0 -bottom-1 h-[2px] bg-orange-500 transition-all duration-300 ${
      active ? "w-full" : "w-0 group-hover:w-full"
    }`;

  const profileImage =
    user?.profile_picture ||
    user?.profile_image ||
    user?.avatar ||
    user?.image ||
    null;

  const profileImageSrc = profileImage
    ? profileImage.startsWith("http")
      ? profileImage
      : `${process.env.REACT_APP_IMG_URL}/${profileImage}`
    : null;

  const desktopAuthContent = authLoading ? null : !isAuthenticated ? (
    <Link
      to="/login"
      className="border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition px-4 py-2 rounded-full font-semibold"
    >
      Connexion
    </Link>
  ) : (
    <div className="relative" ref={profileRef}>
      <button
        type="button"
        onClick={() => setIsProfileOpen((prev) => !prev)}
        className="group flex items-center gap-3 px-3 py-2 rounded-full bg-black/40 hover:bg-black/55 border border-white/10 transition-all duration-300 shadow-lg"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shrink-0">
          {profileImageSrc ? (
            <img
              src={profileImageSrc}
              alt="Photo de profil"
              className="w-full h-full object-cover"
            />
          ) : (
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 6.75a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.118a7.5 7.5 0 0115 0A17.933 17.933 0 0112 21.75a17.933 17.933 0 01-7.5-1.632z"
              />
            </svg>
          )}
        </div>

        <div className="flex flex-col text-left leading-tight">
          <span className="text-sm font-semibold text-white">
           { user.firstname || "Mon compte"}
          </span>
          <span className="text-[11px] text-gray-300">
            Espace personnel
          </span>
        </div>

        <svg
          className={`w-4 h-4 text-gray-300 transition-transform duration-300 ${
            isProfileOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isProfileOpen && (
        <div className="absolute right-0 top-full mt-3 w-60 bg-white rounded-2xl shadow-2xl text-gray-900 overflow-hidden z-[120]">
          <Link
            to="/profile"
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-100 transition"
          >
            <span>Profil</span>
          </Link>

          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 w-full text-left px-4 py-3 hover:bg-gray-100 transition"
          >
            <span>Se déconnecter</span>
          </button>
        </div>
      )}
    </div>
  );

  const mobileAuthContent = authLoading ? null : !isAuthenticated ? (
    <li>
      <Link to="/login" className="text-orange-500 font-bold">
        Connexion
      </Link>
    </li>
  ) : (
    <>
      <li>
        <Link to="/profile" className="text-white">
          Profil
        </Link>
      </li>
      <li>
        <button type="button" onClick={handleLogout} className="text-white">
          Se déconnecter
        </button>
      </li>
    </>
  );

  return (
    <>
      <header
        className="relative bg-black text-white z-[100]"
        style={{
          backgroundImage: `url(${bannerImage})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "cover",
          marginTop: "-8px",
        }}
      >
        <div className="absolute inset-0 bg-black/45" />

        <div className="relative w-11/12 xl:w-4/5 mx-auto">
          <div className="flex justify-center items-center pt-4 pb-2">
            <Link to="/" className="flex justify-center items-center">
              <img
                src={logoFront}
                alt="LMFP Photography Logo"
                className="h-32 md:h-40 lg:h-48 w-auto drop-shadow-xl"
              />
            </Link>
          </div>

          <div className="backdrop-blur-sm bg-black/35 border border-white/10 rounded-2xl px-4 md:px-8 py-4 shadow-2xl mb-6 relative z-[110]">
            <div className="flex items-center justify-between">
              <nav className="hidden md:flex items-center justify-center w-full">
                <ul className="flex items-center gap-8 lg:gap-12 text-lg lg:text-xl font-semibold">
                  <li className="group relative">
                    <Link to="/" className={navLinkClass(isExactActive("/"))}>
                      Accueil
                      <span className={navUnderlineClass(isExactActive("/"))} />
                    </Link>
                  </li>

                  <li className="group relative">
                    <Link
                      to="/posts"
                      className={navLinkClass(isSectionActive("/posts"))}
                    >
                      Articles
                      <span
                        className={navUnderlineClass(isSectionActive("/posts"))}
                      />
                    </Link>
                  </li>

                  <li className="group relative">
                    <Link
                      to="/galerie"
                      className={navLinkClass(isSectionActive("/galerie"))}
                    >
                      Galerie
                      <span
                        className={navUnderlineClass(isSectionActive("/galerie"))}
                      />
                    </Link>
                  </li>

                  <li className="group relative">
                    <Link
                      to="/contact"
                      onClick={handleContactClick}
                      className={navLinkClass(isExactActive("/contact"))}
                    >
                      Contact
                      <span
                        className={navUnderlineClass(isExactActive("/contact"))}
                      />
                    </Link>
                  </li>
                </ul>
              </nav>

              <div className="hidden md:flex items-center gap-4 absolute right-6">
                {desktopAuthContent}
              </div>

              <div className="md:hidden flex justify-between items-center w-full">
                <span className="text-lg font-semibold">Menu</span>
                <button
                  type="button"
                  data-mobile-toggle
                  onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                  className="p-2 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 transition"
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    {isMobileMenuOpen ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {isMobileMenuOpen && (
              <div
                ref={mobileMenuRef}
                className="md:hidden mt-4 bg-black/50 rounded-2xl p-4 border border-white/10"
              >
                <ul className="flex flex-col gap-4 text-base font-semibold">
                  <li>
                    <Link
                      to="/"
                      className={isExactActive("/") ? "text-orange-500" : "text-white"}
                    >
                      Accueil
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/posts"
                      className={
                        isSectionActive("/posts") ? "text-orange-500" : "text-white"
                      }
                    >
                      Articles
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/galerie"
                      className={
                        isSectionActive("/galerie") ? "text-orange-500" : "text-white"
                      }
                    >
                      Galerie
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/contact"
                      onClick={handleContactClick}
                      className={
                        isExactActive("/contact") ? "text-orange-500" : "text-white"
                      }
                    >
                      Contact
                    </Link>
                  </li>

                  {mobileAuthContent}
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>

      {isContactPromptOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setIsContactPromptOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-[#111] text-white p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold mb-3 text-center">
              Accès au contact
            </h2>

            <p className="text-gray-300 text-center mb-6 leading-relaxed">
              Pour accéder au formulaire de contact, vous devez être connecté à
              votre compte.
            </p>

            <div className="flex flex-col gap-3">
              <Link
                to="/login"
                className="w-full text-center px-6 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 transition font-semibold"
                onClick={() => setIsContactPromptOpen(false)}
              >
                Se connecter
              </Link>

              <Link
                to="/register"
                className="w-full text-center px-6 py-3 rounded-xl border border-white/15 hover:bg-white/10 transition font-semibold"
                onClick={() => setIsContactPromptOpen(false)}
              >
                Créer un compte
              </Link>

              <button
                type="button"
                onClick={() => setIsContactPromptOpen(false)}
                className="w-full text-center px-6 py-3 rounded-xl text-gray-400 hover:text-white transition"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;